<template>
  <JazelAppContainer
    appName="Scan2Sell"
    @update:account-settings="accountSettings.setAccountSettings($event)"
    @update:session-token="accountSettings.setSessionToken($event)"
    @update:is-client-user="accountSettings.setIsClientUser($event)"
    @update:user-role="accountSettings.setRole($event)"
    @update:product-settings="productSettings.setProductSettings($event)"
    data-testid="jazel-app-container"
  >
    <!-- QR Code Print Component -->
    <QrCodePrint
      :car-data="selectedVehicles"
      v-if="!qrCodeStore.qrCodeLoading && qrCodeStore.qrCodeContent.length > 0"
    />

    <!-- Main Application Interface -->
    <div
      fluid
      v-else-if="
        qrCodeStore.qrCodeLoading || qrCodeStore.qrCodeContent.length === 0
      "
    >
      <div class="d-flex align-center justify-space-between">
        <!-- Tabs for different sections -->
        <v-tabs v-model="activeTab" class="d-flex justify-space-between">
          <v-tab value="dashboard" data-testid="dashboard-tab">
            <v-icon start class="mr-sm-2">mdi-view-dashboard</v-icon>
            <span>Dashboard</span>
          </v-tab>
          <v-tab
            value="printSettings"
            data-testid="print-settings-tab"
            v-if="hasAdminAccess"
          >
            <v-icon start class="mr-sm-2">mdi-printer</v-icon>
            <span>Print Settings</span>
          </v-tab>
        </v-tabs>
      </div>

      <!-- Tabs Content -->
      <v-tabs-window v-model="activeTab">
        <!-- dashboard view -->
        <v-tabs-window-item value="dashboard" v-if="inventoryIds.length > 0">
          <AppDashboard
            :selected-vehicles="selectedVehicles"
            @print="handlePrint"
            @selection-change="handleSelected"
            @clear-selection="clearSelection"
          >
            <template #inventory-interface>
              <JazelInventoryInterface
                :account-ids="inventoryIds"
                :available-filter-groups="[
                  'conditions',
                  'years',
                  'make',
                  'model',
                  'trim',
                ]"
                :service-endpoint="inventoryServiceEndpoint"
                :juid="juid"
                :product="product"
                :modelValue="searchResults"
                :show-filters-ui="true"
                :show-items-ui="true"
                :selectable="true"
                :selected-items="selectedVehicles"
                @update:selected="handleSelected"
                :key="componentKey"
              />
            </template>
          </AppDashboard>
        </v-tabs-window-item>
        <v-tabs-window-item value="printSettings" v-if="hasAdminAccess">
          <PrintSettings />
        </v-tabs-window-item>
      </v-tabs-window>
    </div>
  </JazelAppContainer>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import {
  JazelAppContainer,
  JazelInventoryInterface,
  useJazelAppConfig,
} from '@jazel/jazel-vuetify'
import { useQrCodeStore } from './store/qrCodeStore'
import PrintSettings from './components/settings/PrintSettings.vue'
import { useProductSettingsStore } from './store/productSettings'
import QrCodePrint from './components/QrCodePrint.vue'
import { getAccountId, getInventoryApi } from '@/utils'
import { useAccountSettingsStore } from '@/store/accountSettings'
import AppDashboard from './components/dashboard/AppDashboard.vue'

const productSettings = useProductSettingsStore()
const accountSettings = useAccountSettingsStore()
const appConfig = useJazelAppConfig()

const qrCodeStore = useQrCodeStore()
const activeTab = ref('dashboard')
const searchResults = ref()
const selectedVehiclesVins = ref<any[]>([])
const selectedVehicles = ref([])
const componentKey = ref(0)

watch(
  () => accountSettings.role,
  newRole => {
    if (
      activeTab.value === 'printSettings' &&
      (!newRole || !hasAdminAccess.value)
    ) {
      activeTab.value = 'dashboard'
    }
  },
)

const inventoryIds = computed(() => {
  return accountSettings.inventoryIds ?? []
})

// Strict role-based access: only admins/owners can access Print Settings
const currentRole = computed(() =>
  String(accountSettings.role || '').toLowerCase(),
)
const hasAdminAccess = computed(() => {
  return (
    currentRole.value === 'company_admin' ||
    currentRole.value === 'company_owner' ||
    currentRole.value === 'company_suadmin' ||
    currentRole.value === 'client_admin'
  )
})

const inventoryServiceEndpoint = getInventoryApi()
const juid = computed(() => {
  const value = String(accountSettings.accountId || getAccountId() || '').trim()
  return value !== '' ? value : undefined
})
const product = computed(
  () => String(appConfig.productId || '').trim() || 'scan2sell',
)

const handleSelected = (data: any) => {
  selectedVehicles.value = data.map((item: any) => {
    const mappedItem = {
      vin: item.vin,
      stockNumber: item.stockNumber,
      conditions: item.conditions || item.condition,
      year: item.year,
      make: item.make,
      model: item.model,
      trim: item.trim,
      price: item.price || item.display_price,
      evBatteryRange: item.evBatteryRange,
      evMpgCombined: item.evMpgCombined,
      mileage: item.mileage,
      inStockTimestamp: item.inStockTimestamp,
      discount: item.discount,
    }
    return mappedItem
  })
  selectedVehiclesVins.value = data.map((item: any) => item.vin)
}

const clearSelection = () => {
  selectedVehicles.value = []
  selectedVehiclesVins.value = []
}

const handlePrint = () => {
  if (selectedVehicles.value.length === 0) return
  qrCodeStore.generateQrCode(selectedVehiclesVins.value)
}
</script>

<style scoped>
#scan2sell-admin-ui :deep(.v-label.v-field-label) {
  margin-left: 12px !important;
}

#scan2sell-admin-ui :deep(.v-slide-group__content) {
  justify-content: center;
}

.v-select .v-field .v-field__input > input {
  background: none;
  border: none;
  box-shadow: none;
}

:deep(.v-field__input) {
  background: none;
  border: none;
}

:deep(.v-field--variant-outlined .v-field__input) {
  padding: 12px 16px;
}

:deep(.v-field--variant-filled .v-field__input) {
  box-shadow: none;
  line-height: 1.625em;
  padding: 24px 16px 4px 16px;
}

:deep(.v-field--variant-filled .v-field__input input) {
  background: none;
  border: none;
  box-shadow: none;
}

:deep(
  .v-field.v-field--variant-outlined .v-field__field input[type='text']:focus
) {
  box-shadow: none !important;
}

:deep(
  .v-field.v-field--variant-outlined .v-field__field input[type='number']:focus
) {
  box-shadow: none !important;
}
</style>
